'use client';

import { useEffect, useMemo, useState } from 'react';
import { getMoreItems, getPagedItems, IPaginationInfo, IPaginationItem } from './pagination.service';

export function usePagination<T extends IPaginationItem = IPaginationItem>(
  items: T[],
  options?: {
    mode?: 'pagination' | 'infinite'; // default to pagination
    page?: number;
    perPage?: number;
    delayInMs?: number;
  }): IPaginationInfo<T> & {
    loadMore: () => number;
    goToPage: (page: number) => void;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    setPerPage: (perPage: number) => void;
  } {
  const mode = options?.mode || 'pagination';
  const delayInMs = options?.delayInMs || 100;
  const [page, setPage] = useState(options?.page || 1);
  const [perPage, setPerPageInner] = useState(options?.perPage || 12);
  const [more, setMore] = useState(0);
  const total = items.length;
  const pages = Math.ceil(total / perPage);
  const hasMore = page < pages;
  const prevPage = page > 1 ? page - 1 : undefined;
  const nextPage = page < pages ? page + 1 : undefined;

  const pagedItems = useMemo(() => {
    const pagedItems = mode === 'pagination' ?
      getPagedItems(items, page, perPage) :
      getMoreItems(items, page, perPage);
    return pagedItems;
  }, [items, mode, page, perPage]);

  function goToPage(page: number) {
    if (page > 0 && page <= pages) {
      setPage(page);
    }
  }

  function goToNextPage() {
    goToPage(page + 1);
  }

  function goToPreviousPage() {
    goToPage(page - 1);
  }

  function setPerPage(perPage: number) {
    if (perPage > 0) {
      const pages = Math.ceil(total / perPage);
      const newPage = Math.max(1, Math.min(pages, page));
      setPage(newPage);
      setPerPageInner(perPage);
    }
  }

  function loadMore() {
    if (hasMore) {
      if (mode === 'infinite') {
        setMore(more + 1);
      } else {
        goToNextPage();
      }
      return page + 1;
    } else {
      return page;
    }
  }

  useEffect(() => {
    // console.log('usePagination', hasMore, more, total, position);
    if (mode === 'infinite' && hasMore && more > 0) {
      const timer = setTimeout(() => {
        setPage(page + 1);
      }, delayInMs);
      return () => {
        clearTimeout(timer);
      };
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [more, delayInMs]);

  return {
    items: pagedItems,
    hasMore,
    nextPage,
    page,
    pages,
    perPage,
    prevPage,
    total,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    loadMore,
    setPerPage,
  };
}
